import Link from "next/link";
import type { LeaderboardEntry } from "@/lib/conditions/board";
import { scoreBadgeColor } from "@/lib/conditions/scoring";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { skiLabel10 } from "@/lib/conditions/ski-labels-v3";
import { SaveToCompanionButton } from "./SaveToCompanionButton";
import { companionSpotTypeForCategory } from "./save-to-companion-state";
import { callPreferenceCategoryForSport } from "./call-preference-options";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";
import {
  namedSupLeaderboardDisplayLabel,
  namedSupLeaderboardSafetyState,
} from "@/lib/conditions/sup-profile-leaderboard-presentation";
import {
  ScoreConfidenceBadge,
  scoreReadingWithConfidenceRange,
} from "./ScoreConfidenceBadge";
import type { SkiScoreConfidenceMetadata } from "@/lib/conditions/ski-score-confidence";
import {
  hasSkiV3,
  isSkiWithheld,
  podiumSkiSport,
  skiClassMarker,
  skiExtentCompact,
  skiLikelyRange,
  skiScoreTooltip,
} from "./ski-board-display";
import { SkiClassChip, SkiExtentChip } from "./SkiScoreChips";

export type PodiumTheme = "alpine" | "coastal";
export type PodiumCategory =
  | "piste"
  | "offpiste"
  | "touring"
  | "surf"
  | "wind"
  | "foil"
  | "downwind-foil"
  | "wing-foil"
  | "diving"
  | "swim"
  | "sup"
  | "sup-touring"
  | "sup-surf"
  | "sup-downwind"
  | "sailing"
  | "mtb"
  | "bodyboarding";

function scoreLabel(
  score: number,
  theme: PodiumTheme,
  category?: PodiumCategory
): { className: string; label: string } {
  const skiSport = category ? podiumSkiSport(category) : null;
  if (theme === "alpine") {
    // Shared platform bands (9/7.5/6.5/5/3.5/2) pick the colour; ski sports take their
    // own vocabulary (piste, or the natural one for off-piste and touring).
    const tier = alpineTier(score);
    return { className: tier.className, label: skiSport ? skiLabel10(skiSport, score) : tier.label };
  }
  const badge = scoreBadgeColor(score);
  return { className: `${badge.bg} ${badge.text}`, label: skiSport ? skiLabel10(skiSport, score) : badge.label };
}

/** Alpine badge tiers on the shared platform bands; the label is the platform word (colour only for ski). */
function alpineTier(score: number): { className: string; label: string } {
  if (score >= 9) return { className: "ski-badge-firing", label: "Hero" };
  if (score >= 7.5) return { className: "ski-badge-firing", label: "Firing" };
  if (score >= 6.5) return { className: "ski-badge-great", label: "Great" };
  if (score >= 5) return { className: "ski-badge-good", label: "Good" };
  if (score >= 3.5) return { className: "ski-badge-fair", label: "Fair" };
  if (score >= 2) return { className: "ski-badge-fair", label: "Poor" };
  return { className: "ski-badge-flat", label: "Flat" };
}

function getScore(entry: LeaderboardEntry, category: PodiumCategory): number {
  switch (category) {
    case "piste":
      return entry.pisteScore ?? entry.conditionsScore;
    case "offpiste":
      return entry.offpisteScore ?? entry.conditionsScore;
    case "touring":
      return entry.touringScore ?? entry.conditionsScore;
    default:
      return entry.conditionsScore;
  }
}

function snowDepthConfidence(
  entry: LeaderboardEntry,
  category: PodiumCategory,
): SkiScoreConfidenceMetadata | null {
  if (category === "piste") {
    return entry.snowDepthConfidenceBySport?.piste ?? null;
  }
  if (category === "offpiste") {
    return entry.snowDepthConfidenceBySport?.["off-piste"] ?? null;
  }
  if (category === "touring") {
    return entry.snowDepthConfidenceBySport?.touring ?? null;
  }
  return null;
}

function getKeyStat(
  entry: LeaderboardEntry,
  category: PodiumCategory
): { value: string; label: string } | null {
  switch (category) {
    case "piste":
    case "offpiste": {
      if (entry.snowfallCm48h != null && entry.snowfallCm48h > 0)
        return { value: `${entry.snowfallCm48h.toFixed(0)}cm`, label: "fresh snow" };
      if (entry.snowDepthCm != null && entry.snowDepthCm > 0)
        return { value: `${entry.snowDepthCm}cm`, label: "base depth" };
      return null;
    }
    case "touring": {
      if (entry.snowfallCm48h != null && entry.snowfallCm48h > 0)
        return { value: `${entry.snowfallCm48h.toFixed(0)}cm`, label: "fresh snow" };
      if (entry.rawWindSpeedKmh != null)
        return { value: `${entry.rawWindSpeedKmh.toFixed(0)} km/h`, label: "wind" };
      return null;
    }
    case "surf":
    case "sup":
    case "sup-touring":
    case "sup-surf":
    case "sup-downwind": {
      if (entry.swellWaveHeightM != null)
        return { value: `${entry.swellWaveHeightM.toFixed(1)}m`, label: "swell" };
      return null;
    }
    case "wind": {
      if (entry.rawWindSpeedKmh != null)
        return { value: `${entry.rawWindSpeedKmh.toFixed(0)} km/h`, label: "wind" };
      return null;
    }
    case "foil": {
      if (entry.swellWaveHeightM != null)
        return { value: `${entry.swellWaveHeightM.toFixed(1)}m`, label: "wave" };
      return null;
    }
    case "downwind-foil": {
      if (entry.rawWindSpeedKmh != null)
        return { value: `${entry.rawWindSpeedKmh.toFixed(0)} km/h`, label: "wind" };
      return null;
    }
    case "wing-foil": {
      if (entry.rawWindSpeedKmh != null)
        return { value: `${entry.rawWindSpeedKmh.toFixed(0)} km/h`, label: "wind" };
      return null;
    }
    case "diving":
    case "swim": {
      if (entry.waterTempC != null)
        return { value: `${entry.waterTempC.toFixed(0)}°C`, label: "water" };
      return null;
    }
    case "sailing": {
      if (entry.rawWindSpeedKmh != null)
        return { value: `${entry.rawWindSpeedKmh.toFixed(0)} km/h`, label: "wind" };
      return null;
    }
    case "mtb": {
      if (entry.rawWindSpeedKmh != null)
        return { value: `${entry.rawWindSpeedKmh.toFixed(0)} km/h`, label: "wind" };
      return null;
    }
    case "bodyboarding": {
      if (entry.swellWaveHeightM != null)
        return { value: `${entry.swellWaveHeightM.toFixed(1)}m`, label: "swell" };
      return null;
    }
    default:
      return null;
  }
}

function podiumSubtitle(category: PodiumCategory): string {
  switch (category) {
    case "piste":
      return "Top 3 piste conditions right now";
    case "offpiste":
      return "Top 3 off-piste conditions right now";
    case "touring":
      return "Top 3 touring conditions right now";
    case "surf":
      return "Top 3 surf conditions right now";
    case "wind":
      return "Top 3 wind conditions right now";
    case "foil":
      return "Top 3 prone foil conditions right now";
    case "downwind-foil":
      return "Top 3 downwind foil conditions right now";
    case "wing-foil":
      return "Top 3 wing foil conditions right now";
    case "diving":
      return "Top 3 diving conditions right now";
    case "swim":
      return "Top 3 swim conditions right now";
    case "sup":
      return "Top 3 SUP conditions right now";
    case "sup-touring":
      return "Top 3 Touring SUP conditions right now";
    case "sup-surf":
      return "Top 3 SUP Surf conditions right now";
    case "sup-downwind":
      return "Top 3 Downwind SUP conditions right now";
    case "sailing":
      return "Top 3 sailing conditions right now";
    case "mtb":
      return "Top 3 trail conditions right now";
    case "bodyboarding":
      return "Top 3 bodyboarding conditions right now";
    default:
      return "Top 3 conditions right now";
  }
}

// Sport lens carried in the fragment of links into the shared spot page.
function boardLensSport(category: PodiumCategory): string | null {
  switch (category) {
    case "piste": return "piste";
    case "offpiste": return "off-piste";
    case "touring": return "touring";
    case "wind": return "wind";
    case "foil": return "foil";
    case "downwind-foil": return "downwind-foil";
    case "wing-foil": return "wing-foil";
    case "diving": return "diving";
    case "sup": return "sup-touring";
    case "sup-touring": return "sup-touring";
    case "sup-surf": return "sup-surf";
    case "sup-downwind": return "sup-downwind";
    case "swim": return "swimming";
    case "mtb": return "mtb";
    case "bodyboarding": return "bodyboarding";
    default: return null;
  }
}

function PodiumCard({
  entry,
  rank,
  basePath,
  category,
  theme,
}: {
  entry: LeaderboardEntry;
  rank: number;
  basePath: string;
  category: PodiumCategory;
  theme: PodiumTheme;
}) {
  const score = getScore(entry, category);
  const badge = scoreLabel(score, theme, category);
  const isFirst = rank === 1;
  const keyStat = getKeyStat(entry, category);
  const sport = boardLensSport(category);
  const href = sport ? `${basePath}/${entry.slug}#sport=${sport}` : `${basePath}/${entry.slug}`;
  const saveSpotType = companionSpotTypeForCategory(category);
  const callPreference = {
    spotName: entry.name,
    category: callPreferenceCategoryForSport(category),
    allowedSupProfiles: isSupProfileKey(category) ? [category] : undefined,
  };
  const namedSafetyState = isSupProfileKey(category)
    ? namedSupLeaderboardSafetyState(entry)
    : null;
  const depthConfidence = snowDepthConfidence(entry, category);
  const conditionsLabel = isSupProfileKey(category)
    ? namedSupLeaderboardDisplayLabel(entry)
    : badge.label;
  // v3 ski scores: likely range and class words in the tooltip, class marker and the piste
  // open-terrain note beside the score. Revision 2 rows keep the inline range and badge.
  const skiSport = podiumSkiSport(category);
  const skiV3 = skiSport !== null && hasSkiV3(entry);
  const skiTooltip = skiV3 ? skiScoreTooltip(conditionsLabel, entry) : null;
  const skiRange = skiV3 ? skiLikelyRange(entry.lo, entry.hi) : null;
  const skiMarker = skiV3 ? skiClassMarker(entry.cls) : null;
  const extentCompact = skiV3 && skiSport === "piste" ? skiExtentCompact(entry.extent) : null;

  return (
    <article
      className={`group relative flex flex-col items-center rounded-[20px] border text-center transition-all ${
        isFirst
          ? theme === "alpine"
            ? "border-[rgba(75,163,195,0.3)] shadow-[0_12px_48px_rgba(75,163,195,0.12)] md:pb-7 pb-5 pt-6 md:pt-7 px-5 md:px-8 bg-gradient-to-b from-white via-white to-[rgba(75,163,195,0.03)]"
            : "border-[rgba(13,148,136,0.3)] shadow-[0_12px_48px_rgba(13,148,136,0.12)] md:pb-7 pb-5 pt-6 md:pt-7 px-5 md:px-8 bg-gradient-to-b from-white via-white to-[rgba(13,148,136,0.03)]"
          : theme === "alpine"
            ? "bg-white border-border-light hover:border-[rgba(75,163,195,0.25)] hover:shadow-[0_8px_32px_rgba(75,163,195,0.08)] pb-3 pt-3 md:pt-3.5 px-3 md:px-5"
            : "bg-white border-border-light hover:border-[rgba(13,148,136,0.25)] hover:shadow-[0_8px_32px_rgba(13,148,136,0.08)] pb-3 pt-3 md:pt-3.5 px-3 md:px-5"
      }`}
    >
      {isFirst && (
        <>
          <div
            className={`pointer-events-none absolute inset-0 rounded-[20px] ${
              theme === "alpine"
                ? "bg-[radial-gradient(ellipse_at_top,rgba(75,163,195,0.04),transparent_70%)]"
                : "bg-[radial-gradient(ellipse_at_top,rgba(13,148,136,0.04),transparent_70%)]"
            }`}
          />
          <div
            className={`pointer-events-none absolute -top-3 left-1/2 z-10 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[1.5px] text-white ${
              theme === "alpine"
                ? "bg-[#4BA3C3] shadow-[0_2px_8px_rgba(75,163,195,0.3)]"
                : "bg-[#0D9488] shadow-[0_2px_8px_rgba(13,148,136,0.3)]"
            }`}
          >
            #1 Today
          </div>
        </>
      )}
      <Link
        href={href}
        className="relative flex w-full flex-1 flex-col items-center text-center no-underline"
      >
        <div
          className={`flex items-center justify-center rounded-full font-serif font-bold tabular-nums ${
            isFirst
              ? theme === "alpine"
                ? "w-11 h-11 md:w-12 md:h-12 text-[22px] md:text-[26px] bg-[#4BA3C3] text-white shadow-[0_4px_12px_rgba(75,163,195,0.3)]"
                : "w-11 h-11 md:w-12 md:h-12 text-[22px] md:text-[26px] bg-[#0D9488] text-white shadow-[0_4px_12px_rgba(13,148,136,0.3)]"
              : theme === "alpine"
                ? "w-7 h-7 md:w-8 md:h-8 text-[14px] md:text-[15px] bg-[rgba(75,163,195,0.08)] text-[#4BA3C3]"
                : "w-7 h-7 md:w-8 md:h-8 text-[14px] md:text-[15px] bg-[rgba(13,148,136,0.08)] text-[#0D9488]"
          }`}
        >
          {rank}
        </div>

        <h3
          className={`font-serif font-bold text-navy leading-tight transition-colors ${
            isFirst ? "mt-2.5 text-[18px] md:text-[22px]" : "mt-1.5 text-[14px] md:text-[15px]"
          } ${
            theme === "alpine"
              ? "group-hover:text-[#4BA3C3]"
              : "group-hover:text-[#0D9488]"
          }`}
        >
          {entry.name}
        </h3>
        <span className="text-[11px] text-text-muted mt-0.5">
          {entry.country}
        </span>

        {namedSafetyState && namedSafetyState !== "clear" && (
          <span
            className={`mt-1.5 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
              namedSafetyState === "flagged-red"
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : namedSafetyState === "flagged-amber"
                  ? "border-amber-200 bg-amber-50 text-amber-800"
                  : "border-slate-200 bg-slate-50 text-slate-700"
            }`}
          >
            {namedSafetyState === "flagged-red"
              ? "Red safety flag"
              : namedSafetyState === "flagged-amber"
                ? "Caution"
                : "Safety evidence unavailable"}
          </span>
        )}

        <div
          className={`rounded-[10px] px-2.5 py-1.5 ${badge.className} ${isFirst ? "mt-2.5" : "mt-2"}`}
          title={skiTooltip ?? undefined}
        >
          <div
            className={`font-serif font-bold leading-none tabular-nums ${
              isFirst ? "text-[24px] md:text-[30px]" : "text-[17px] md:text-[20px]"
            }`}
          >
            {skiV3
              ? scoreOutOf100(score)
              : scoreReadingWithConfidenceRange(
                scoreOutOf100(score),
                depthConfidence?.lo,
                depthConfidence?.hi,
              )}
          </div>
          <div className="text-[8px] uppercase tracking-[0.8px] font-semibold mt-0.5 opacity-90">
            {conditionsLabel}
          </div>
          {skiRange && <span className="sr-only">{`. ${skiRange}`}</span>}
        </div>

        {skiV3 ? (
          (skiMarker || extentCompact) && (
            <span className={`flex flex-wrap justify-center gap-1.5 ${isFirst ? "mt-2" : "mt-1.5"}`}>
              <SkiClassChip fields={entry} label={conditionsLabel} />
              {skiSport === "piste" && <SkiExtentChip extent={entry.extent} />}
            </span>
          )
        ) : depthConfidence && (
          <span className={isFirst ? "mt-2" : "mt-1.5"}>
            <ScoreConfidenceBadge {...depthConfidence} />
          </span>
        )}

        {keyStat && (
          <div className={`flex items-baseline gap-1 ${isFirst ? "mt-2" : "mt-1.5"}`}>
            <span
              className={`font-bold text-navy ${
                isFirst ? "text-[16px] md:text-[18px]" : "text-[13px] md:text-[14px]"
              }`}
            >
              {keyStat.value}
            </span>
            <span className="text-[10px] text-text-muted">{keyStat.label}</span>
          </div>
        )}

        {entry.powderAlert && (category === "piste" || category === "offpiste") && (
          <span className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-[10px] font-semibold">
            Powder alert
          </span>
        )}
      </Link>
      <SaveToCompanionButton
        id={entry.slug}
        spotType={saveSpotType}
        tone="compact"
        className={isFirst ? "mt-3" : "mt-2"}
        callPreference={callPreference}
      />
    </article>
  );
}

export function PodiumSection({
  entries,
  basePath,
  category = "piste",
  theme = "alpine",
  compact = false,
  title = "Today’s podium",
}: {
  entries: LeaderboardEntry[];
  basePath: string;
  category?: PodiumCategory;
  theme?: PodiumTheme;
  compact?: boolean;
  title?: string;
}) {
  // A withheld ski score (Closed, or No lift access) never reaches the podium.
  const skiSport = podiumSkiSport(category);
  const podium = skiSport
    ? entries.filter((entry) => !isSkiWithheld(skiSport, entry))
    : entries;
  if (podium.length === 0) return null;

  return (
    <section className={compact ? "py-5 border-y border-border-light" : "px-5 md:px-12 py-8 md:py-10 bg-warm-bg border-b border-border-light"}>
      <div className="max-w-[1100px] mx-auto">
        <div className="flex items-baseline justify-between mb-5 md:mb-6">
          <h2 className="font-serif text-[22px] md:text-[28px] font-bold text-navy">
            {title}
          </h2>
          <span className="text-[11px] text-text-muted hidden sm:block">
            {podiumSubtitle(category)}
          </span>
        </div>

        <div className={`grid grid-cols-1 gap-2.5 md:gap-3 ${podium.length > 1 ? "md:grid-cols-3" : ""}`}>
          {podium.slice(0, 3).map((entry, index) => <PodiumCard key={entry.slug} entry={entry} rank={index + 1} basePath={basePath} category={category} theme={theme} />)}
        </div>
      </div>
    </section>
  );
}
